import React from 'react';
import { CanvasState, CanvasGuides, TextElement, RectElement } from '../types';
import { genId } from './DesignerToolbar';

interface Template {
  id: string;
  name: string;
  description: string;
  preview: string;
  build: (w: number, h: number, title: string, author: string, guides: CanvasGuides) => Partial<CanvasState>;
}

/**
 * All element positions are placed within the FRONT panel of the full cover canvas.
 * frontX = guides.frontStartPx
 * frontW = stageWidth - guides.frontStartPx - guides.bleedPx
 * innerH = stageHeight - 2 * guides.bleedPx
 */
const TEMPLATES: Template[] = [
  {
    id: 'blank',
    name: 'Blank',
    description: 'Start from scratch',
    preview: '#ffffff',
    build: () => ({
      background: '#ffffff',
      elements: [],
    }),
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'White background with centred title and author on the front panel',
    preview: '#f9fafb',
    build: (w, h, title, author, guides) => {
      const fx = guides.frontStartPx;
      const fw = w - guides.frontStartPx - guides.bleedPx;
      const fy = guides.bleedPx;
      const fh = h - 2 * guides.bleedPx;
      const pad = fw * 0.08;
      return {
        background: '#f9fafb',
        elements: [
          {
            id: genId(), type: 'text', name: 'Title',
            x: fx + pad, y: fy + fh * 0.35,
            width: fw - 2 * pad, height: 60,
            rotation: 0, opacity: 1, visible: true,
            text: title || 'Book Title',
            fontSize: Math.round(fw * 0.09), fontFamily: 'Georgia',
            fontStyle: 'bold', fill: '#111827', align: 'center',
          } as TextElement,
          {
            id: genId(), type: 'text', name: 'Author',
            x: fx + pad, y: fy + fh * 0.82,
            width: fw - 2 * pad, height: 32,
            rotation: 0, opacity: 1, visible: true,
            text: author ? `by ${author}` : 'Author Name',
            fontSize: Math.round(fw * 0.045), fontFamily: 'Georgia',
            fontStyle: 'italic', fill: '#6b7280', align: 'center',
          } as TextElement,
        ],
      };
    },
  },
  {
    id: 'dark',
    name: 'Dark',
    description: 'Dark background with light typography on the front panel',
    preview: '#111827',
    build: (w, h, title, author, guides) => {
      const fx = guides.frontStartPx;
      const fw = w - guides.frontStartPx - guides.bleedPx;
      const fy = guides.bleedPx;
      const fh = h - 2 * guides.bleedPx;
      const pad = fw * 0.08;
      return {
        background: '#111827',
        elements: [
          {
            id: genId(), type: 'text', name: 'Title',
            x: fx + pad, y: fy + fh * 0.30,
            width: fw - 2 * pad, height: 70,
            rotation: 0, opacity: 1, visible: true,
            text: title || 'Book Title',
            fontSize: Math.round(fw * 0.10), fontFamily: 'Georgia',
            fontStyle: 'bold', fill: '#f9fafb', align: 'center',
          } as TextElement,
          {
            id: genId(), type: 'text', name: 'Author',
            x: fx + pad, y: fy + fh * 0.80,
            width: fw - 2 * pad, height: 32,
            rotation: 0, opacity: 1, visible: true,
            text: author || 'Author Name',
            fontSize: Math.round(fw * 0.05), fontFamily: 'Georgia',
            fontStyle: 'normal', fill: '#9ca3af', align: 'center',
          } as TextElement,
        ],
      };
    },
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Orange header band on the front panel — IndiePress brand style',
    preview: 'linear-gradient(135deg, #f97316 50%, #f9fafb 50%)',
    build: (w, h, title, author, guides) => {
      const fx = guides.frontStartPx;
      const fw = w - guides.frontStartPx - guides.bleedPx;
      const fy = guides.bleedPx;
      const fh = h - 2 * guides.bleedPx;
      const pad = fw * 0.08;
      return {
        background: '#f9fafb',
        elements: [
          {
            id: genId(), type: 'rect', name: 'Header Band',
            x: fx, y: fy, width: fw, height: fh * 0.55,
            rotation: 0, opacity: 1, visible: true,
            fill: '#f97316', stroke: '', strokeWidth: 0, cornerRadius: 0,
          } as RectElement,
          {
            id: genId(), type: 'text', name: 'Title',
            x: fx + pad, y: fy + fh * 0.18,
            width: fw - 2 * pad, height: 70,
            rotation: 0, opacity: 1, visible: true,
            text: title || 'Book Title',
            fontSize: Math.round(fw * 0.09), fontFamily: 'Georgia',
            fontStyle: 'bold', fill: '#ffffff', align: 'center',
          } as TextElement,
          {
            id: genId(), type: 'text', name: 'Author',
            x: fx + pad, y: fy + fh * 0.72,
            width: fw - 2 * pad, height: 32,
            rotation: 0, opacity: 1, visible: true,
            text: author || 'Author Name',
            fontSize: Math.round(fw * 0.05), fontFamily: 'Georgia',
            fontStyle: 'italic', fill: '#374151', align: 'center',
          } as TextElement,
        ],
      };
    },
  },
  {
    id: 'split',
    name: 'Split',
    description: 'Two-tone split layout on the front panel',
    preview: 'linear-gradient(to bottom, #111827 50%, #ffffff 50%)',
    build: (w, h, title, author, guides) => {
      const fx = guides.frontStartPx;
      const fw = w - guides.frontStartPx - guides.bleedPx;
      const fy = guides.bleedPx;
      const fh = h - 2 * guides.bleedPx;
      const pad = fw * 0.08;
      return {
        background: '#ffffff',
        elements: [
          {
            id: genId(), type: 'rect', name: 'Top Half',
            x: fx, y: fy, width: fw, height: fh * 0.5,
            rotation: 0, opacity: 1, visible: true,
            fill: '#111827', stroke: '', strokeWidth: 0, cornerRadius: 0,
          } as RectElement,
          {
            id: genId(), type: 'text', name: 'Title',
            x: fx + pad, y: fy + fh * 0.18,
            width: fw - 2 * pad, height: 70,
            rotation: 0, opacity: 1, visible: true,
            text: title || 'Book Title',
            fontSize: Math.round(fw * 0.09), fontFamily: 'Georgia',
            fontStyle: 'bold', fill: '#ffffff', align: 'center',
          } as TextElement,
          {
            id: genId(), type: 'text', name: 'Author',
            x: fx + pad, y: fy + fh * 0.60,
            width: fw - 2 * pad, height: 32,
            rotation: 0, opacity: 1, visible: true,
            text: author || 'Author Name',
            fontSize: Math.round(fw * 0.05), fontFamily: 'Georgia',
            fontStyle: 'normal', fill: '#374151', align: 'center',
          } as TextElement,
        ],
      };
    },
  },
];

interface TemplatesPickerProps {
  stageWidth: number;
  stageHeight: number;
  guides: CanvasGuides;
  bookTitle: string;
  bookAuthor: string;
  onApply: (partial: Partial<CanvasState>) => void;
  onClose: () => void;
}

const TemplatesPicker: React.FC<TemplatesPickerProps> = ({
  stageWidth, stageHeight, guides, bookTitle, bookAuthor, onApply, onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-xl mx-4 border border-gray-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Choose a Template</h2>
            <p className="text-sm text-gray-500 mt-0.5">Elements are placed on the front panel. You can extend them to the back and spine after applying.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >×</button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {TEMPLATES.map(t => (
            <button
              key={t.id}
              onClick={() => {
                onApply(t.build(stageWidth, stageHeight, bookTitle, bookAuthor, guides));
                onClose();
              }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-orange-400 hover:shadow-md transition-all group text-left"
            >
              <div
                className="w-full h-20 rounded-lg border border-gray-200"
                style={{ background: t.preview }}
              />
              <div className="w-full">
                <p className="text-sm font-medium text-gray-800 group-hover:text-orange-600">{t.name}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-snug">{t.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemplatesPicker;
